import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { portalAnalyticsApi, portalDashboardApi } from '../../services/api';
import { billingApi } from '../../services/billing';
import { usePortalStore } from '../../stores';
import { NexusCard } from '../../components/nexus-ui';
import { Lightning, ChartLine, ChatCircle, Wallet } from '@phosphor-icons/react';
import { formatMoney } from '../../lib/geo';

const CHART_THEME = {
  grid: 'rgba(255,255,255,0.04)',
  text: '#A0AEC0',
  steel: '#1E4E8C',
  ember: '#F26B1F',
  amber: '#92580A',
  blue: '#2E70AD',
};

const TIME_RANGES = ['7d', '30d', '90d'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-nexus-text-tertiary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-medium" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, accent = '#1E4E8C' }) => (
  <NexusCard variant="glass" className="p-4">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ background: `${accent}1A`, border: `1px solid ${accent}40` }}>
        <Icon className="w-5 h-5" weight="duotone" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold tracking-wider uppercase text-nexus-text-tertiary">{label}</p>
        <p className="text-xl font-heading font-bold text-nexus-text-primary truncate">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-[11px] text-nexus-text-tertiary">{sub}</p>}
      </div>
    </div>
  </NexusCard>
);

// 1 credit = 1000 tokens base; this converts tokens to "base credits" (no model multiplier).
const tokensToBaseCredits = (tokens) => Math.ceil((tokens || 0) / 100) / 10; // round up to 0.1

const PortalAnalytics = () => {
  const [range, setRange] = useState('30d');
  const userId = usePortalStore((s) => s.user?.id);

  const { data: stats } = useQuery({
    queryKey: ['portalStats', userId],
    queryFn: () => portalDashboardApi.getStats().then((r) => r.data),
    enabled: !!userId,
  });
  const { data: timeseries = [] } = useQuery({
    queryKey: ['portalTimeseries', range, userId],
    queryFn: () => portalAnalyticsApi.getTimeseries(range).then((r) => r.data),
    enabled: !!userId,
  });
  const { data: wallet } = useQuery({
    queryKey: ['portalWallet', userId],
    queryFn: () => billingApi.wallet().then((r) => r.data),
    refetchInterval: 30000,
    enabled: !!userId,
  });

  const totalTokens = timeseries.reduce((s, d) => s + (d.tokens || 0), 0);
  const totalCalls = timeseries.reduce((s, d) => s + (d.api_calls || 0), 0);
  const totalConvos = timeseries.reduce((s, d) => s + (d.conversations || 0), 0);
  const baseCredits = tokensToBaseCredits(totalTokens);
  const currency = wallet?.currency || 'INR';
  const effectiveBalance = (wallet?.effective_balance ?? wallet?.credit_balance) || 0;

  // Approximate spend = base credits * current bracket rate (rough — multipliers vary per call)
  const approxSpend = (() => {
    if (!wallet) return 0;
    const rate = currency === 'INR' ? 9.5 : 0.58; // bracket-0 rate; refined display
    return baseCredits * rate;
  })();

  return (
    <div className="space-y-6" data-testid="portal-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>USAGE</div>
          <h2 className="font-heading text-3xl font-bold text-nexus-text-primary mt-1">Analytics</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">Tokens, credits, calls and conversations.</p>
        </div>
        <div className="flex gap-1 p-1 bg-nexus-bg-tertiary/50 rounded-lg border border-white/5">
          {TIME_RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                range === r ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint' : 'text-nexus-text-tertiary hover:text-nexus-text-secondary'
              }`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles — credit-aware */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Lightning}
          label="Tokens"
          value={totalTokens.toLocaleString()}
          sub={`≈ ${baseCredits.toFixed(1)} base credits`}
          accent="#F26B1F"
        />
        <MetricCard
          icon={ChartLine}
          label="API Calls"
          value={totalCalls}
          sub={`${totalConvos.toLocaleString()} conversations`}
          accent="#1E4E8C"
        />
        <MetricCard
          icon={Wallet}
          label="Credit Balance"
          value={`${effectiveBalance.toFixed(1)} cr`}
          sub={wallet ? `Bracket ${wallet.bracket} · ${formatMoney(currency === 'INR' ? 9.5 : 0.58, currency)}/cr` : '—'}
          accent="#10B981"
        />
        <MetricCard
          icon={ChatCircle}
          label="Approx. Spend"
          value={formatMoney(approxSpend, currency)}
          sub="At base bracket rate"
          accent="#92580A"
        />
      </div>

      {/* Token Consumption */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <NexusCard variant="glass" hover={false}>
          <div className="p-6">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary">Token Consumption</h3>
              <span className="text-[11px] text-nexus-text-tertiary">PAYG · no monthly limit</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timeseries} margin={{ top: 10, right: 16, left: 16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                <YAxis
                  width={56}
                  tick={{ fontSize: 10, fill: CHART_THEME.text }}
                  axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <defs>
                  <linearGradient id="pInputGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_THEME.steel} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={CHART_THEME.steel} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="pOutputGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_THEME.ember} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={CHART_THEME.ember} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area dataKey="input_tokens" name="Input Tokens" stroke={CHART_THEME.steel} fill="url(#pInputGrad)" strokeWidth={2} />
                <Area dataKey="output_tokens" name="Output Tokens" stroke={CHART_THEME.ember} fill="url(#pOutputGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </NexusCard>
      </motion.div>

      {/* API Calls + Conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">API Calls</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={timeseries} margin={{ top: 10, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis width={48} tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="api_calls" name="API Calls" fill={CHART_THEME.ember} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">Conversations</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={timeseries} margin={{ top: 10, right: 16, left: 16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis width={48} tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="conversations" name="Conversations" fill={CHART_THEME.blue} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>
      </div>
    </div>
  );
};

export default PortalAnalytics;
